import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, ShieldCheck } from "lucide-react";
import { EASE, Reveal, scrollToId } from "./Weave";

const PERSONAL = [
  "A structured exploration of your next chapter",
  "The Founder Capacity Index assessment",
  "A peer cohort and relationships that last",
  "No obligation to continue — complete in its own right",
];

const FULL = {
  upfront: { price: "£3,000", note: "one payment, inc. VAT" },
  monthly: { price: "£300", note: "per month — £3,600 total, inc. VAT" },
};

export default function Tuition() {
  const [plan, setPlan] = useState("upfront");

  return (
    <section id="tuition" data-testid="tuition-section" className="scroll-mt-24">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
        <Reveal>
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-wred">Tuition</p>
          <h2 className="max-w-3xl text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Honest numbers. No small-print games.
          </h2>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-body2">
            Only continue if it&rsquo;s right for you — and only if you can genuinely afford the
            commitment, in time and money. We mean that.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          <Reveal delay={0.05}>
            <div className="flex h-full flex-col rounded-3xl border border-paperborder bg-white p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-tealdark">Where everyone starts</p>
              <h3 className="mt-3 text-2xl font-bold text-ink">Personal Clarity</h3>
              <p className="mt-6 flex items-baseline gap-2">
                <span className="text-5xl font-extrabold tracking-tight text-ink">£499</span>
                <span className="text-base font-medium text-muted2">inc. VAT · 10 weeks</span>
              </p>
              <ul className="mt-8 space-y-4">
                {PERSONAL.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-body2">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teallight">
                      <Check className="h-3 w-3 text-tealdark" strokeWidth={3} />
                    </span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid="tuition-personal-cta"
                onClick={() => scrollToId("waitlist")}
                className="group mt-10 inline-flex items-center justify-center gap-2 rounded-full border-2 border-ink px-7 py-3.5 text-base font-semibold text-ink transition-colors hover:bg-ink hover:text-paper"
              >
                Start with Personal Clarity
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="relative flex h-full flex-col overflow-hidden rounded-3xl bg-ink p-8 text-paper lg:p-10">
              <div
                className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full opacity-25 blur-3xl"
                style={{ background: "radial-gradient(circle, #4AA0A4 0%, transparent 70%)" }}
                aria-hidden="true"
              />
              <div className="relative flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-wyellow">The full Academy year</p>
                <div data-testid="tuition-plan-toggle" className="flex rounded-full border border-paper/25 p-1">
                  {Object.entries({ upfront: "Pay in full", monthly: "Monthly" }).map(([key, label]) => (
                    <button
                      key={key}
                      data-testid={`tuition-toggle-${key}`}
                      onClick={() => setPlan(key)}
                      className={`relative rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                        plan === key ? "text-ink" : "text-paper/70 hover:text-paper"
                      }`}
                    >
                      {plan === key && (
                        <motion.span
                          layoutId="tuition-toggle-pill"
                          className="absolute inset-0 rounded-full bg-wyellow"
                          transition={{ duration: 0.4, ease: EASE }}
                        />
                      )}
                      <span className="relative">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <h3 className="relative mt-3 text-2xl font-bold">Founder Academy</h3>
              <p className="relative mt-6 flex items-baseline gap-2">
                <motion.span
                  key={plan}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="text-5xl font-extrabold tracking-tight"
                >
                  {FULL[plan].price}
                </motion.span>
                <span className="text-base font-medium text-paper/60">{FULL[plan].note}</span>
              </p>

              <ul className="relative mt-8 space-y-4">
                {[
                  "Builds directly on your Personal Clarity work — no restart",
                  "Business Clarity, then External Clarity: build the business, then the stories that matter",
                  "Build the entire revenue engine to scale",
                  "The Weave's 1% Club — a lifetime peer network, free, after the Academy",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-paper/85">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-wyellow/20">
                      <Check className="h-3 w-3 text-wyellow" strokeWidth={3} />
                    </span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="relative mt-10 rounded-2xl bg-wyellow p-5 text-ink">
                <p className="text-sm font-bold uppercase tracking-[0.15em]">Early-bird</p>
                <p className="mt-1.5 text-sm leading-relaxed sm:text-base">
                  £300 deposit + £2,300 balance by 31 December —{" "}
                  <strong className="font-extrabold">£400 saved</strong> on the full year.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <p className="mt-10 flex items-start gap-3 text-sm leading-relaxed text-muted2 sm:text-base">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-tealb" />
            If you continue after Personal Clarity, the remaining year is £2,501 — £3,000 total. Every
            place carries a 14-day cancellation policy with a full refund.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
